import React, { useEffect, useRef, useState } from 'react';
import { Button, Card, Drawer, InputRef, message, Modal, Space, Table, Tag } from "antd";
import { EyeOutlined, ReloadOutlined } from "@ant-design/icons";
import { useSearchParams } from "react-router-dom";
import { ColumnsType } from "antd/es/table";

import { getRingLogSteps, getRingLogSleep, getRingLogMeasurements, RingLogsBaseParams, RingLogsStepsParams, RingLogsMeasurementsParams } from "services/ring-logs";
import CustomBreadcrumb from "components/CustomBreadcrumb";
import RingLogsFilters from "../components/RingLogsFilters";
import { RingLogsFilter } from "../types";
import { objectToUrlParams, urlParamsToObject } from "../../../utils/url-utils";
import { history } from "../../../utils/history";
import { getColumnSearchProps } from "../../../components/TableSearch";
import { MeasurementKey } from "../../../services/ring-logs";

interface RingLogRow {
    key: string;
    _type: 'steps' | 'sleep' | 'measurements';
    _measurementKey?: string;
    id: number;
    timestamp?: string;
    [k: string]: any;
}

const DEFAULT_PAGE_SIZE = 50;
const ALL_TYPES_PAGE_SIZE = 500;

const TYPE_COLORS: Record<string, string> = {
    steps: 'green',
    sleep: 'blue',
    measurements: 'orange',
};

const RingLogsListPage = () => {
    const [searchParams] = useSearchParams();

    const [ringLogs, setRingLogs] = useState<RingLogRow[]>([]);
    const [selectedRecordKey, setSelectedRecordKey] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(false);

    const [filters, setFilters] = useState<RingLogsFilter>();

    const [isModalOpen, setIsModalOpen] = useState(false);

    const [totalItems, setTotalItems] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

    const searchInput = useRef<InputRef>(null);
    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');

    const [open, setOpen] = useState(false);

    useEffect(() => {
        fetchData();
    }, [currentPage, pageSize, filters]);

    useEffect(() => {
        const filtersInUrl = urlParamsToObject<RingLogsFilter>(searchParams);
        if (filtersInUrl && (filtersInUrl.macAddress || filtersInUrl.deviceId)) {
            setFilters(prev => ({
                ...prev,
                ...filtersInUrl,
                deviceId: filtersInUrl.deviceId ? Number(filtersInUrl.deviceId) : undefined,
            }));
        } else {
            setOpen(true);
        }
    }, [searchParams]);

    const showDrawer = () => setOpen(true);
    const onClose = () => setOpen(false);

    const showModal = () => setIsModalOpen(true);
    const handleOk = () => { setIsModalOpen(false); setSelectedRecordKey(null); };
    const handleCancel = () => { setIsModalOpen(false); setSelectedRecordKey(null); };

    const buildBaseParams = (overridePageSize?: number): RingLogsBaseParams | null => {
        if (!filters) return null;
        const hasIdentifier = filters.macAddress || filters.deviceId;
        if (!hasIdentifier) return null;

        return {
            ...(filters.macAddress ? { mac_address: filters.macAddress } : {}),
            ...(filters.deviceId ? { device_id: Number(filters.deviceId) } : {}),
            ...(filters.startDate ? { start_date: filters.startDate } : {}),
            ...(filters.endDate ? { end_date: filters.endDate } : {}),
            page: overridePageSize ? 1 : currentPage,
            page_size: overridePageSize || pageSize,
        };
    };

    const fetchData = async () => {
        const type = filters?.type || 'all';
        const isAll = type === 'all';
        const baseParams = buildBaseParams(isAll ? ALL_TYPES_PAGE_SIZE : undefined);
        if (!baseParams) return;

        setLoading(true);
        try {
            let allItems: RingLogRow[] = [];
            let totalCount = 0;

            if (isAll) {
                const [stepsRes, sleepRes, measRes] = await Promise.all([
                    getRingLogSteps({ ...baseParams }),
                    getRingLogSleep(baseParams),
                    getRingLogMeasurements({
                        ...baseParams,
                        ...(filters?.measurementKey ? { measurement_key: filters.measurementKey as MeasurementKey } : {}),
                    }),
                ]);

                allItems = [
                    ...(stepsRes.items || []).map((item: any, idx: number) => ({
                        ...item, key: `ring-steps-${item.id ?? idx}`, _type: 'steps' as const,
                    })),
                    ...(sleepRes.items || []).map((item: any, idx: number) => ({
                        ...item, key: `ring-sleep-${item.id ?? idx}`, _type: 'sleep' as const,
                    })),
                    ...(measRes.items || []).map((item: any, idx: number) => ({
                        ...item, key: `ring-meas-${item.id ?? idx}`, _type: 'measurements' as const,
                        _measurementKey: item.measurement_key || item.measurementKey || null,
                    })),
                ];
                totalCount = allItems.length;
            } else {
                if (type === 'steps') {
                    const stepsParams: RingLogsStepsParams = { ...baseParams };
                    const stepsRes = await getRingLogSteps(stepsParams);
                    allItems = (stepsRes.items || []).map((item: any, idx: number) => ({
                        ...item, key: `ring-steps-${item.id ?? idx}`, _type: 'steps' as const,
                    }));
                    totalCount = stepsRes.count || 0;
                }

                if (type === 'sleep') {
                    const sleepRes = await getRingLogSleep(baseParams);
                    allItems = (sleepRes.items || []).map((item: any, idx: number) => ({
                        ...item, key: `ring-sleep-${item.id ?? idx}`, _type: 'sleep' as const,
                    }));
                    totalCount = sleepRes.count || 0;
                }

                if (type === 'measurements') {
                    const measParams: RingLogsMeasurementsParams = {
                        ...baseParams,
                        ...(filters?.measurementKey ? { measurement_key: filters.measurementKey as MeasurementKey } : {}),
                    };
                    const measRes = await getRingLogMeasurements(measParams);
                    allItems = (measRes.items || []).map((item: any, idx: number) => ({
                        ...item, key: `ring-meas-${item.id ?? idx}`, _type: 'measurements' as const,
                        _measurementKey: item.measurement_key || item.measurementKey || null,
                    }));
                    totalCount = measRes.count || 0;
                }
            }

            setRingLogs(allItems);
            setTotalItems(totalCount);
        } catch (err) {
            console.error('Error getting ring logs', err);
            message.error('Failed to get ring logs');
        }
        setLoading(false);
    };

    const handleViewRecordDetails = (record: RingLogRow) => {
        setSelectedRecordKey(record.key);
        showModal();
    };

    const handleOnFilter = (values: RingLogsFilter) => {
        setCurrentPage(1);
        setFilters(values);
        setOpen(false);
        const url = objectToUrlParams(values);
        history?.navigate?.(url);
    };

    const columns: ColumnsType<RingLogRow> = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            ...getColumnSearchProps({
                searchInput,
                setSearchText,
                setSearchedColumn,
                searchedColumn,
                searchText,
                dataIndex: 'id',
            }),
        },
        {
            title: 'Type',
            dataIndex: '_type',
            key: '_type',
            render: (value: string) => (
                <Tag color={TYPE_COLORS[value] || 'default'}>{value}</Tag>
            ),
        },
        {
            title: 'Measurement Type',
            dataIndex: '_measurementKey',
            key: '_measurementKey',
            render: (value: string | null | undefined) => value || '—',
        },
        {
            title: 'Timestamp',
            dataIndex: 'timestamp',
            key: 'timestamp',
            render: (_: string, record: RingLogRow) => {
                const display = record._type === 'sleep' ? record.night : record.timestamp;
                return <>{display || '—'}</>;
            },
        },
        {
            title: 'Action',
            dataIndex: 'action',
            key: 'action',
            render: (_: any, record: RingLogRow) => (
                <Button
                    onClick={() => handleViewRecordDetails(record)}
                    type="primary"
                    icon={<EyeOutlined />}
                />
            ),
        },
    ];

    const selectedRecord = ringLogs.find(item => item.key === selectedRecordKey) || null;

    return (
        <>
            <CustomBreadcrumb list={['Ring logs', 'List']} />
            <Card
                title="Ring logs"
                extra={
                    <Space>
                        <Button
                            loading={loading}
                            onClick={() => fetchData()}
                            icon={<ReloadOutlined />}
                        />
                        <Button type="primary" loading={loading} onClick={showDrawer}>
                            Filters
                        </Button>
                    </Space>
                }
            >
                <Table<RingLogRow>
                    columns={columns}
                    dataSource={ringLogs}
                    loading={loading}
                    size="small"
                    bordered
                    pagination={{
                        pageSize,
                        current: currentPage,
                        total: totalItems,
                        showSizeChanger: true,
                        pageSizeOptions: ['10', '20', '30', '50', '100'],
                        showTotal: (total, range) =>
                            `Showing ${range[0]}-${range[1]} of ${total} items`,
                    }}
                    onChange={(pagination) => {
                        setPageSize(pagination.pageSize || DEFAULT_PAGE_SIZE);
                        setCurrentPage(pagination.current || 1);
                    }}
                />
            </Card>
            <Drawer
                title="Filter"
                placement="left"
                closable={false}
                onClose={onClose}
                open={open}
                key="DrawerFilter"
            >
                <RingLogsFilters onSubmit={handleOnFilter} filters={filters} loading={loading} />
            </Drawer>
            <Modal title="Your JSON Data" open={isModalOpen} onOk={handleOk} onCancel={handleCancel}>
                <pre>{selectedRecord && JSON.stringify(selectedRecord, null, 2)}</pre>
            </Modal>
        </>
    );
};

export default RingLogsListPage;
